<template>
    <v-dialog v-model="internalValue" max-width="650px" persistent scrollable>
      <v-card>
        <v-card-title class="headline grey lighten-3 d-flex align-center justify-space-between py-3">
          <span class="text-h6 font-weight-bold text-uppercase primary--text">
            {{ isNewEntry ? 'Create New' : 'Edit' }} {{ formatName(table) }}
          </span>
          <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
  
        <v-card-text class="pt-4 pb-0" style="max-height: 70vh;">
          <v-container class="pa-0">
            <v-form ref="form" v-model="valid">
              <v-row dense>
                <v-col 
                  v-for="col in columns" 
                  :key="col" 
                  :cols="getColSpan(col)"
                >
                  <!-- 1. RELATIONSHIPS / FOREIGN KEYS -->
                  <AutoCompleteCrudAdvanced
                    v-if="relations[col]"
                    v-model="editedItem[col]"
                    :table="relations[col].relationTable || relations[col]"
                    :label="getRelationLabel(col)"
                    @input="(val) => { editedItem[col] = val ? val.id : null }"
                  ></AutoCompleteCrudAdvanced>
  
                  <!-- 2. DATE FIELD -->
                  <v-menu
                    v-else-if="isDateColumn(col)"
                    :ref="'menu_' + col"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :close-on-content-click="true"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="editedItem[col]"
                        :label="formatLabel(col)"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                        clearable
                      ></v-text-field>
                    </template>
                    <v-date-picker 
                      v-model="editedItem[col]" 
                      no-title 
                      scrollable
                    ></v-date-picker>
                  </v-menu>
  
                  <!-- 3. COLOR FIELD -->
                  <template v-else-if="isColorColumn(col)">
                    <p class="mb-1 grey--text text--darken-1 subtitle-2">{{ formatLabel(col) }}</p>
                    <v-color-picker
                      v-model="editedItem[col]"
                      flat
                      dot-size="20"
                      swatches-max-height="120"
                      class="mb-3"
                    ></v-color-picker>
                  </template>
  
                  <!-- 4. TEXTAREA (For long summaries/descriptions) -->
                  <v-textarea
                    v-else-if="isTextareaColumn(col)"
                    v-model="editedItem[col]"
                    :label="formatLabel(col)"
                    outlined
                    dense
                    auto-grow
                    rows="3"
                  ></v-textarea>
  
                  <!-- 5. STANDARD TEXTFIELD / NUMBER -->
                  <v-text-field
                    v-else
                    v-model="editedItem[col]"
                    :label="formatLabel(col)"
                    :type="isNumericColumn(col) ? 'number' : 'text'"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
  
        <v-divider></v-divider>
  
        <v-card-actions class="pa-4 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text color="secondary" @click="close" :disabled="saving">
            Cancel
          </v-btn>
          <v-btn 
            color="success" 
            depressed 
            :loading="saving" 
            :disabled="!valid" 
            @click="save"
          >
            {{ isNewEntry ? 'Create Record' : 'Save Changes' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import axios from "@/plugins/axios";
  import helpersJs from "@/plugins/helpers";
  
  export default {
    name: "DataEntryAndEdit",
    props: {
      // Standard v-model structure to control modal show/hide state
      value: { type: Boolean, default: false }, 
      table: { type: String, required: true },
      row: { type: Object, default: () => ({}) },
      // Omit lists specific to any table
      globalOmittedFields: {
        type: Array,
        default: () => ['id', 'emis', 'created_at', 'updated_at', 'case_docs_folder', 'invoice_no', 'sapsCaseNo','trial']
      }
    },
    data: () => ({
      valid: true,
      saving: false,
      columns: [],
      relations: {},
      editedItem: {},
    }),
    computed: {
      internalValue: {
        get() { return this.value; },
        set(val) { this.$emit('input', val); }
      },
      getAccessToken() {
        return this.$store.getters.getAccessToken
      },
      isNewEntry() {
        return !this.row || !this.row.id;
      }
    },
    watch: {
      async value(val) {
        if (val) {
          await this.initializeEditor();
        }
      }
    },
    async mounted(){
      await this.initializeEditor()
    },
    methods: {
      async initializeEditor() {
        await this.fetchTableMetadata();
        this.populateItem();
      },
  
      async fetchTableMetadata() {
        try {
          const res = await axios.postDataWithToken("/auth/get-table-items-with-table-name", {
            table: this.table
          }, this.getAccessToken);
        
          if (res && res.status === "success") {
            // Filter out the omitted administrative fields
            this.columns = (res.columns || []).filter(
              col => !this.globalOmittedFields.includes(col)
            );
            this.relations = res.relations || {};
          }
        } catch (e) {
          console.error("Failed fetching table metadata", e);
        }
      },
  
      populateItem() {
        if (this.isNewEntry) {
            let newItem = {};
            if(this.row){
                newItem = { ...this.row };
            }
          
          this.columns.forEach(col => {
            if(this.isNumericColumn(col)){
                newItem[col] = newItem[col] ? newItem[col] : 0;
            }else{
                newItem[col] = newItem[col] ? newItem[col] : '';
            }
          });
          this.editedItem = newItem;
        } else {
          this.editedItem = { ...this.row };
        }
      },
  
      // Layout helper: make long text elements block out full-width on desk/mobile
      getColSpan(col) {
        if (this.isTextareaColumn(col) || this.isColorColumn(col)) {
          return 12;
        }
        return "12 sm6"; // 1 column on mobile, 2 columns on desktop
      },
  
      formatName(name) {
        return helpersJs.formatUnderscoreNamesWithFirstCapitalLetters(name);
      },
  
      formatLabel(col) {
        if (col === "dob") return "Date of Birth";
        return col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      },
  
      getRelationLabel(col) {
        if (col === 'branch_id') return 'Select Local Branch';
        return this.formatLabel(col);
      },
  
      isDateColumn(col) {
        const datesColumns = ['birthday', 'dob', 'expiry', 'start', 'end','valid_from','valid_to'];
        const column = col.toLowerCase();
        return column.includes('date') || datesColumns.includes(column);
      },
  
      isColorColumn(col) {
        return col.toLowerCase().includes('color');
      },
  
      isNumericColumn(col) {
        return col.toLowerCase().includes("percent") || col.toLowerCase().includes('amount') || col.toLowerCase().includes('balance') || col.toLowerCase().includes('total');
      },
  
      isTextareaColumn(col) {
        const longTextFields = ['summary', 'description', 'notes', 'merits', 'comments'];
        return longTextFields.some(term => col.toLowerCase().includes(term));
      },
  
      close() {
        this.internalValue = false;
        this.$emit('close');
      },
  
      async save() {
        if (this.$refs.form && !this.$refs.form.validate()) return;
        this.saving = true;
  
        try {
          const dataToSave = { ...this.editedItem };
  
          // Normalize color structures to hex
          Object.keys(dataToSave).forEach(key => {
            if (this.isColorColumn(key) && typeof dataToSave[key] === 'object') {
              dataToSave[key] = dataToSave[key].hex;
            }
          });
  
          // Trigger dynamic API save endpoint matching your design pattern
          const res = await axios.postDataWithToken(`/auth/add-edit-delete-autocomplete`, {
            ...dataToSave,
            table: this.table,
            mode: this.isNewEntry ? 'add' : 'edit'
          }, this.getAccessToken);
  
          if (res && res.status === 'success') {
            this.$emit('saved', res.data);
            this.close();
          }
        } catch (e) {
          console.error("Save process failed", e);
        } finally {
          this.saving = false;
        }
      }
    }
  };
  </script>